import { Suspense, useEffect, useState } from "react";
import "./App.css";
import Home from "./views/Home";
import Container from "./components/Container";
import AppContextProvider from "./context/AppContextProvider";
import './i18n';
import { ModalProvider } from "./components/Modal";
import Loader from "./components/Loader";
import { ToastProvider } from "./components/Toast";
import { getSystemStatusMessages } from "./lib/api/api";
import { ISystemStatusMessage } from "./@types/systemStatus";
import { isBlockingSystemStatus, sortSystemStatusMessages, SystemStatusBanner, SystemStatusMaintenancePage } from "./components/SystemStatus";
import { optionalEnv } from "./lib/env";

const dismissedMessagesStorageKey = "cosmogas-lp-dismissed-system-status-messages";

function getDismissedMessageIds() {
	try {
		return JSON.parse(window.localStorage.getItem(dismissedMessagesStorageKey) ?? "[]") as string[];
	} catch {
		return [];
	}
}

function isSystemStatusMessageActive(message: ISystemStatusMessage, now = new Date()) {
	if (!message.startAt || !message.endAt) {
		return false;
	}

	const startAt = new Date(message.startAt);
	const endAt = new Date(message.endAt);

	if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
		return false;
	}

	return now >= startAt && now <= endAt;
}

export default function App() {
	const [systemStatusMessages, setSystemStatusMessages] = useState<ISystemStatusMessage[]>([]);
	const [blockingSystemStatusMessage, setBlockingSystemStatusMessage] = useState<ISystemStatusMessage | null>(null);
	const [isSystemStatusLoaded, setIsSystemStatusLoaded] = useState(false);
	const [dismissedMessageIds, setDismissedMessageIds] = useState<string[]>(getDismissedMessageIds);

	useEffect(() => {
		if (optionalEnv.workInProgress) {
			return;
		}

		let isMounted = true;

		async function loadSystemStatus() {
			const response = await getSystemStatusMessages("lp");

			if (!isMounted) return;

			if (!response.error) {
				const now = new Date();
				const activeMessages = sortSystemStatusMessages(
					(response.data ?? []).filter((message) => isSystemStatusMessageActive(message, now))
				);
				const blockingMessage = activeMessages.find((message) => message.severity === "critical")
					?? activeMessages.find((message) => message.severity === "error")
					?? null;

				setBlockingSystemStatusMessage(blockingMessage);
				setSystemStatusMessages(activeMessages.filter((message) => !isBlockingSystemStatus(message)));
			}

			setIsSystemStatusLoaded(true);
		}

		loadSystemStatus();

		return () => {
			isMounted = false;
		}
	}, []);

	function dismissSystemStatusMessage(messageId: string) {
		const nextDismissedMessageIds = Array.from(new Set([...dismissedMessageIds, messageId]));
		window.localStorage.setItem(dismissedMessagesStorageKey, JSON.stringify(nextDismissedMessageIds));
		setDismissedMessageIds(nextDismissedMessageIds);
	}

	if (optionalEnv.workInProgress) {
		return <SystemStatusMaintenancePage />
	}

	if (!isSystemStatusLoaded) {
		return (
			<Container>
				<Loader />
			</Container>
		)
	}

	if (blockingSystemStatusMessage) {
		return <SystemStatusMaintenancePage message={blockingSystemStatusMessage} />
	}

	const visibleSystemStatusMessages = systemStatusMessages.filter((message) => (
		!dismissedMessageIds.includes(message.id)
	));

	return (
		<Suspense fallback={
			<Container>
				<Loader />
			</Container>
		}>
			<AppContextProvider>
				<ToastProvider>
					<ModalProvider>
						{visibleSystemStatusMessages.map((message) => (
							<SystemStatusBanner
								key={message.id}
								message={message}
								onDismiss={() => dismissSystemStatusMessage(message.id)}
							/>
						))}
						<Container>
							<Home />
						</Container>
					</ModalProvider>
				</ToastProvider>
			</AppContextProvider >
		</Suspense>
	)
}
